import React, { useState, useRef, useEffect } from "react";
import "./index.css";

const buildGreenLetterMap = (wordsList) => {
  const greenLetterMap = {};

  wordsList.forEach((word) => {
    if (!word || !Array.isArray(word.word)) return;

    word.word.forEach((charObj) => {
      if (charObj.color === "green") {
        greenLetterMap[charObj.index] = (charObj.character || "").toUpperCase();
      }
    });
  });

  return greenLetterMap;
};

const applyPersistedGreens = (wordString, greenLetterMap) =>
  wordString.split("").map((char, index) => {
    const shouldBeGreen =
      greenLetterMap[index] && greenLetterMap[index] === char.toUpperCase();

    return {
      character: char,
      color: shouldBeGreen ? "green" : "gray",
      index,
    };
  });

const getLocalDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const getWordsFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const initialWords = [];
  for (let i = 1; i <= 6; i++) {
    const word = (params.get(`w${i}`) || "").toLowerCase().trim();
    if (word.length === 5 && /^[a-z]{5}$/.test(word)) {
      initialWords.push(word);
    }
  }
  return initialWords;
};

function App() {
  const [words, setWords] = useState(() => {
    const base = Date.now();
    return getWordsFromUrl().map((wordStr, i) => ({
      id: base + i,
      string: wordStr,
      word: applyPersistedGreens(wordStr, {}),
    }));
  });
  const [wordInput, setWordInput] = useState("");
  const [suggestedWords, setSuggestedWords] = useState([]);
  const [autopilot, setAutopilot] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [autopilotLoading, setAutopilotLoading] = useState(false);
  const [validateLoading, setValidateLoading] = useState(false);
  const [showAutopilotModal, setShowAutopilotModal] = useState(false);
  const [autopilotStartWord, setAutopilotStartWord] = useState("");
  const [autopilotDate, setAutopilotDate] = useState(getLocalDateString());

  const gridRef = useRef(null);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.scrollTop = gridRef.current.scrollHeight;
    }
  }, [words]);

  const submitWord = (e) => {
    e.preventDefault();
    if (wordInput.length !== 5) return;
    const greenLetterMap = buildGreenLetterMap(words);
    const newWord = {
      id: Date.now(),
      string: wordInput,
      word: applyPersistedGreens(wordInput, greenLetterMap),
    };
    setWords((prevWords) => [...prevWords, newWord]);
    setWordInput("");
    setSuggestedWords([]); // Clear suggestions when adding a new word
  };

  const addSuggestedWord = (word) => {
    const greenLetterMap = buildGreenLetterMap(words);
    const newWord = {
      id: Date.now(),
      string: word,
      word: applyPersistedGreens(word, greenLetterMap),
    };
    setWords((prevWords) => [...prevWords, newWord]);
    setSuggestedWords([]);
  };

  const updateLetter = (wordString, index) => {
    setWords((prevWords) =>
      prevWords.map((w) => {
        if (w.string !== wordString) return w;
        const newWordArray = w.word.map((charObj) => {
          if (charObj.index !== index) return charObj;
          let nextColor;
          switch (charObj.color) {
            case "gray":
              nextColor = "yellow";
              break;
            case "yellow":
              nextColor = "green";
              break;
            case "green":
              nextColor = "gray";
              break;
            default:
              nextColor = "gray";
          }
          return { ...charObj, color: nextColor };
        });
        return { ...w, word: newWordArray };
      })
    );
  };

  const fetchSuggestions = async () => {
    const res = await fetch("/getSuggestedWords", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ words, vowelsFirst: autopilot, selectedDate }),
    });
    let data;
    try {
      data = await res.json();
    } catch (e) {
      data = [];
    }
    if (!Array.isArray(data)) {
      data = [];
    }
    setSuggestedWords(data);
  };

  const toggleAutopilot = (checked) => {
    setAutopilot(checked);
  };

  const changeDate = (date) => {
    setSelectedDate(date);
  };

  const reset = () => {
    setWords([]);
    setSuggestedWords([]);
  };

  const openAutopilotModal = () => {
    setShowAutopilotModal(true);
    setAutopilotStartWord(words.length === 0 ? "audio" : "");
    setAutopilotDate(getLocalDateString());
  };

  const closeAutopilotModal = () => {
    setShowAutopilotModal(false);
    setAutopilotStartWord("");
  };

  const runAutopilot = async () => {
    if (words.length === 0 && autopilotStartWord.length !== 5) {
      alert("Please enter a valid 5-letter starting word");
      return;
    }
    
    setAutopilotLoading(true);
    setShowAutopilotModal(false);
    
    try {
      const res = await fetch("/autopilot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          startingWord: autopilotStartWord,
          selectedDate: autopilotDate,
          words: words
        }),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        data = [];
      }
      if (!Array.isArray(data)) {
        data = [];
      }
      setWords(data);
    } catch (error) {
      console.error("Autopilot error:", error);
      alert("Failed to run autopilot. Please try again.");
    }
    setAutopilotLoading(false);
  };

  const runValidate = async () => {
    if (words.length === 0) return;
    setValidateLoading(true);
    try {
      const res = await fetch("/validateBoard", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ words, selectedDate }),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        data = [];
      }
      if (!Array.isArray(data)) {
        alert(data?.error || "Failed to validate board.");
      } else {
        setWords(data);
      }
    } catch (error) {
      console.error("Validate error:", error);
      alert("Failed to validate board. Please try again.");
    }
    setValidateLoading(false);
  };

  return (
    <div className="container">
      <h1>🎯 Eldrow</h1>
      
      {/* Game Mode Controls */}
      <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f7fafc', borderRadius: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
          <form onSubmit={submitWord} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              value={wordInput}
              onChange={(e) => setWordInput(e.target.value)}
              maxLength="5"
              disabled={autopilotLoading || validateLoading}
              className="input-field"
              placeholder="Enter 5-letter word"
              style={{ width: '180px' }}
              autoComplete="off"
            />
            <button type="submit" disabled={autopilotLoading || validateLoading || wordInput.length !== 5} className="btn btn-primary">
              Add Word
            </button>
          </form>
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => changeDate(e.target.value)}
            max={getLocalDateString()}
            className="input-field"
            style={{ width: '160px', fontSize: '0.9rem', padding: '8px 12px' }}
          />
          <button onClick={reset} disabled={autopilotLoading || validateLoading} className="btn btn-secondary">
            🔄 Reset Game
          </button>
          <button
            onClick={runValidate}
            disabled={autopilotLoading || validateLoading || words.length === 0}
            className="btn btn-success"
          >
            {validateLoading ? '⏳ Validating...' : '✅ Check Board'}
          </button>
          <button 
            onClick={openAutopilotModal} 
            disabled={autopilotLoading || validateLoading}
            className="btn btn-primary"
          >
            🤖 Autopilot
          </button>
        </div>
      </div>

      {/* Autopilot Modal */}
      {showAutopilotModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#4c51bf' }}>🤖 Autopilot Mode</h2>
            
            {words.length === 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Starting Word (5 letters):
              </label>
              <input
                type="text"
                value={autopilotStartWord}
                onChange={(e) => setAutopilotStartWord(e.target.value.toLowerCase())}
                maxLength="5"
                className="input-field"
                placeholder="e.g., audio"
                style={{ width: '100%' }}
                autoFocus
              />
            </div>
            )}

            {words.length > 0 && (
            <p style={{ marginBottom: '1rem', color: '#718096', fontSize: '0.95rem' }}>
              Continuing from the {words.length} word{words.length !== 1 ? 's' : ''} already on the board.
            </p>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Select Date:
              </label>
              <input
                type="date"
                value={autopilotDate}
                onChange={(e) => setAutopilotDate(e.target.value)}
                max={getLocalDateString()}
                className="input-field"
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button 
                onClick={closeAutopilotModal}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={runAutopilot}
                className="btn btn-primary"
                disabled={words.length === 0 && autopilotStartWord.length !== 5}
              >
                Go
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Game Grid */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <div ref={gridRef} id="wordleGrid">
          {words.map((word, idx) => (
            <div key={word.id} className={`word-row ${idx === words.length - 1 ? "active-row" : ""}`}>
              {word.word.map((char) => (
                <button
                  key={char.index}
                  className={`wordle-char wordle-char-${char.color}`}
                  onClick={() => updateLetter(word.string, char.index)}
                >
                  {char.character}
                </button>
              ))}
            </div>
          ))}
        </div>
        
        {/* Check button to the right of grid */}
        {words.length > 0 && (
          <button
            type="button"
            onClick={fetchSuggestions}
            disabled={autopilotLoading}
            className="btn btn-success"
            style={{ height: '52px', alignSelf: 'flex-start', marginTop: '4px' }}
          >
            ✓ Check
          </button>
        )}
      </div>

      {/* Suggested Words */}
      {suggestedWords.length > 0 && (
        <div>
          <h3 style={{ color: '#4c51bf', marginBottom: '0.5rem' }}>💡 Suggested Words</h3>
          <p style={{ fontSize: '0.9rem', color: '#718096', marginBottom: '1rem' }}>
            Click a word to add it to your game
          </p>
          <div className="suggested-words-row">
            {suggestedWords.map((w) => (
              <button
                key={w}
                className="suggested-word"
                onClick={() => addSuggestedWord(w)}
                disabled={autopilotLoading}
              >
                {w}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
